/**
 * sl-mkx.6-rework-3 (F-UX-4): Auto-match workspace credentials to new
 * required credential slots introduced by a template update.
 *
 * Unlike auto-match-fields (which uses name + type scoring across compatible/
 * castable pairs), credential matching is a HARD TYPE FILTER: a credential of
 * service `slack` can never bind a slot of type `openai`. Within same-type
 * candidates, we prefer the lexicographically stable first entry so the
 * preselection is deterministic across re-renders. The user can always
 * override via the dropdown.
 *
 * Each suggestion carries `alternatives` — all same-type workspace credentials
 * — so the UpdateModal can render a select with every eligible option.
 *
 * SOLID:
 * - S: Only binding computation; no UI, no fetching.
 * - D: Depends on pure data shapes, not SWR hooks or services.
 */

export interface CredentialSlot {
  type: string;
  label: string;
  required: boolean;
}

export interface WorkspaceCredentialOption {
  id: string;
  name: string;
  service: string;
}

export interface CredentialMatchAlternative {
  credentialId: string;
  credentialName: string;
}

export interface CredentialMatchSuggestion {
  slotType: string;
  slotLabel: string;
  required: boolean;
  /** Preselected credential id (`null` = nothing matched, user must pick). */
  selectedCredentialId: string | null;
  alternatives: CredentialMatchAlternative[];
}

export function autoMatchCredentials(
  slots: readonly CredentialSlot[],
  availableCredentials: readonly WorkspaceCredentialOption[]
): CredentialMatchSuggestion[] {
  return slots.map(slot => {
    const sameType = availableCredentials.filter(c => c.service === slot.type);
    const sorted = [...sameType].sort((a, b) => a.name.localeCompare(b.name));
    return {
      slotType: slot.type,
      slotLabel: slot.label,
      required: slot.required,
      selectedCredentialId: sorted[0]?.id ?? null,
      alternatives: sorted.map(c => ({
        credentialId: c.id,
        credentialName: c.name,
      })),
    };
  });
}
